import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token, ActionPerformed } from '@capacitor/push-notifications';
import { api } from '../api/apiClient';
import { mobileStorage } from './mobileStorage';

const API_BASE = '/notifications';
const FCM_TOKEN_STORAGE_KEY = 'fcm_token';

let fcmListenersRegistered = false;

/**
 * Converts a base64 string to a Uint8Array.
 * Needed for VAPID public key.
 */
function urlBase64ToUint8Array(base64String: string) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

/**
 * Configure les écouteurs d'actions FCM (clic sur une notification push sur Android).
 */
function ensureFcmActionListeners() {
    if (fcmListenersRegistered || !Capacitor.isNativePlatform()) return;
    fcmListenersRegistered = true;

    PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
        const targetUrl = action.notification?.data?.url;
        if (targetUrl && typeof window !== 'undefined') {
            if (targetUrl.startsWith('/#/')) {
                window.location.hash = targetUrl.substring(1);
            } else if (targetUrl.startsWith('#/')) {
                window.location.hash = targetUrl;
            } else if (targetUrl.startsWith('/')) {
                window.location.hash = `#${targetUrl}`;
            }
        }
    }).catch(() => {});
}

export const pushManager = {
    /**
     * Registers the Service Worker (Web VAPID) or requests FCM permission & token (Android Capacitor).
     */
    async subscribe() {
        // ─── FLUX ANDROID NATIF (FCM via @capacitor/push-notifications) ───
        if (Capacitor.isNativePlatform()) {
            try {
                ensureFcmActionListeners();

                let permStatus = await PushNotifications.checkPermissions();
                if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
                    permStatus = await PushNotifications.requestPermissions();
                }

                if (permStatus.receive !== 'granted') {
                    console.warn('[pushManager] Native push permission not granted:', permStatus.receive);
                    return false;
                }

                const fcmToken = await new Promise<string>((resolve, reject) => {
                    let settled = false;
                    const timeout = setTimeout(() => {
                        if (!settled) {
                            settled = true;
                            reject(new Error('FCM registration timeout'));
                        }
                    }, 15000);

                    PushNotifications.addListener('registration', (token: Token) => {
                        if (!settled) {
                            settled = true;
                            clearTimeout(timeout);
                            resolve(token.value);
                        }
                    });

                    PushNotifications.addListener('registrationError', (err: any) => {
                        if (!settled) {
                            settled = true;
                            clearTimeout(timeout);
                            reject(err);
                        }
                    });

                    PushNotifications.register().catch((err) => {
                        if (!settled) {
                            settled = true;
                            clearTimeout(timeout);
                            reject(err);
                        }
                    });
                });

                await mobileStorage.setItem(FCM_TOKEN_STORAGE_KEY, fcmToken);

                await api.post(`${API_BASE}/push/subscribe`, {
                    platform: 'android',
                    fcmToken,
                    endpoint: `fcm:${fcmToken}`,
                });

                return true;
            } catch (error) {
                console.error('Failed to subscribe to native FCM push notifications:', error);
                throw error;
            }
        }

        // ─── FLUX WEB STANDARD (Service Worker + VAPID) ───
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            console.warn('Push messaging is not supported');
            return null;
        }

        try {
            const registration = await navigator.serviceWorker.ready;
            
            // Get public key from server
            const { data: { publicKey } } = await api.get(`${API_BASE}/push/public-key`);
            
            const subscribeOptions = {
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey)
            };

            const subscription = await registration.pushManager.subscribe(subscribeOptions);
            
            // Send subscription to backend
            const subData = subscription.toJSON();
            await api.post(`${API_BASE}/push/subscribe`, subData);
            
            return true;
        } catch (error) {
            console.error('Failed to subscribe to push notifications:', error);
            throw error;
        }
    },

    /**
     * Unsubscribes the user from push notifications.
     */
    async unsubscribe() {
        // ─── FLUX ANDROID NATIF (FCM) ───
        if (Capacitor.isNativePlatform()) {
            try {
                const fcmToken = await mobileStorage.getItem(FCM_TOKEN_STORAGE_KEY);
                if (fcmToken) {
                    await api.post(`${API_BASE}/push/unsubscribe`, {
                        platform: 'android',
                        fcmToken,
                        endpoint: `fcm:${fcmToken}`,
                    });
                    await mobileStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
                }
                return true;
            } catch (error) {
                console.error('Failed to unsubscribe from native FCM push notifications:', error);
                throw error;
            }
        }

        // ─── FLUX WEB STANDARD (VAPID) ───
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            
            if (subscription) {
                const endpoint = subscription.endpoint;
                await subscription.unsubscribe();
                
                // Notify backend
                await api.post(`${API_BASE}/push/unsubscribe`, { endpoint });
            }
            return true;
        } catch (error) {
            console.error('Failed to unsubscribe from push notifications:', error);
            throw error;
        }
    },

    /**
     * Returns the current subscription status and permission.
     */
    async getStatus() {
        // ─── FLUX ANDROID NATIF (FCM) ───
        if (Capacitor.isNativePlatform()) {
            try {
                const permStatus = await PushNotifications.checkPermissions();
                const savedToken = await mobileStorage.getItem(FCM_TOKEN_STORAGE_KEY);
                const permission =
                    permStatus.receive === 'granted'
                        ? 'granted'
                        : permStatus.receive === 'denied'
                        ? 'denied'
                        : 'default';

                return {
                    supported: true,
                    subscribed: permission === 'granted' && !!savedToken,
                    permission,
                };
            } catch {
                return { supported: false, subscribed: false, permission: 'denied' };
            }
        }

        // ─── FLUX WEB STANDARD (VAPID) ───
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            return { supported: false, subscribed: false, permission: 'denied' };
        }

        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        
        return {
            supported: true,
            subscribed: !!subscription,
            permission: Notification.permission
        };
    }
};
